import { useFormik } from 'formik'
import React, { useState } from 'react'

import {
  Button,
  ButtonGroup,
  Fieldset,
  Popper,
  RadioButton,
  Tag,
  type TagProps,
  useMediaQueryContext,
} from '@fourthwall/components'

import * as S from './StatusTag.styled'
import type { FormValues } from './StatusTag.types'

export type StatusTagProps = {
  tagProps: TagProps
  initialValues?: FormValues
  onSubmit?: (values: FormValues) => void
}

export const StatusTag = ({ tagProps, initialValues, onSubmit }: StatusTagProps) => {
  if (!onSubmit || !initialValues) {
    return (
      <S.Container>
        <Tag {...tagProps} />
      </S.Container>
    )
  }

  const [isOpen, setOpen] = useState(false)
  const media = useMediaQueryContext()

  const formik = useFormik<FormValues>({
    initialValues,
    onSubmit: (values) => {
      onSubmit(values)
      setOpen(false)
    },
    enableReinitialize: true,
  })

  const toggleOpen = () => {
    setOpen((prevIsOpen) => !prevIsOpen)
  }

  const renderForm = () => (
    <S.Form onSubmit={formik.handleSubmit}>
      <Fieldset>
        <Fieldset.Row>
          <RadioButton
            value={formik.values.status}
            checkedValue="Active"
            label="Active"
            suggestion="App is fully functional."
            name="status"
            onChange={formik.handleChange}
          />
        </Fieldset.Row>
        <Fieldset.Row>
          <RadioButton
            value={formik.values.status}
            checkedValue="Inactive"
            label="Inactive"
            suggestion="App is temporarily disabled."
            name="status"
            onChange={formik.handleChange}
          />
        </Fieldset.Row>
      </Fieldset>
      <S.ButtonsContainer>
        <ButtonGroup
          space="medium"
          justify={media.tablet ? 'right' : undefined}
          direction={media.tablet ? 'row' : 'column-reverse'}
          fullWidth={!media.tablet}
        >
          <Button
            type="button"
            label="Cancel"
            fullWidth={!media.tablet}
            onClick={() => {
              formik.resetForm()
              setOpen(false)
            }}
          />
          <Button
            type="button"
            appearance="primary"
            label="Save"
            fullWidth={!media.tablet}
            disabled={!formik.dirty || !formik.isValid}
            onClick={formik.submitForm}
          />
        </ButtonGroup>
      </S.ButtonsContainer>
    </S.Form>
  )

  return (
    <Popper
      isOpen={isOpen}
      referenceComponent={
        <S.Container onClick={toggleOpen}>
          <Tag {...tagProps} />
        </S.Container>
      }
      popperProps={{
        placement: 'bottom-start',
        modifiers: [
          { name: 'preventOverflow', enabled: false },
          { name: 'hide', enabled: false },
        ],
      }}
      style={{ paddingTop: media.tablet ? 8 : 0 }}
      onClose={() => setOpen(false)}
    >
      {renderForm()}
    </Popper>
  )
}
